import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { StandaloneGrid, ModelloDataGrid } from "@/pages/TabelleConfigPage";
import { useAnnoScolastico, formatAnnoScolastico, currentAnnoScolastico } from "@/lib/annoScolastico";

const tabStyle = (active) => ({ padding: "9px 16px", fontSize: 14, fontWeight: 700, border: "none", borderBottom: active ? "3px solid #0d3b66" : "3px solid transparent", background: "transparent", color: active ? "#0d3b66" : "#78909c", cursor: "pointer", marginBottom: -2, whiteSpace: "nowrap" });

// Rimuove l'anno scolastico dal nome tabella: "… 2027 2028", "… 2027/2028", "a.s. 2027/28", "… 2027".
const stripAnno = (nome = "") => (nome || "")
  .replace(/\b(a\.?\s*s\.?\s*)?20\d{2}\s*[\/\-–\s]\s*(?:20)?\d{2}\b/gi, "")
  .replace(/\b20\d{2}\b/g, "")
  .replace(/[\s\/\-–]+$/g, "")
  .replace(/\s{2,}/g, " ")
  .trim();

function TableHost({ modello, anno }) {
  if (!modello) return null;
  return modello.standalone
    ? <StandaloneGrid modello={modello} recordMode anno={anno} titolo={stripAnno(modello.nome)} toolbarRight />
    : <ModelloDataGrid modello={modello} />;
}


export default function OpenDayIscrizioniPage() {
  const [odTables, setOdTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(null); // modello _id (string)
  const { anno: annoGlobale, anniAvailable } = useAnnoScolastico();
  const [annoSel, setAnnoSel] = useState(null);
  const [da, setDa] = useState(() => new Date().toISOString().slice(0, 10));
  const [aDate, setADate] = useState(() => new Date().toISOString().slice(0, 10));
  const [printing, setPrinting] = useState(false);
  useEffect(() => { if (annoSel == null && annoGlobale != null) setAnnoSel(annoGlobale + 1); }, [annoGlobale, annoSel]);
  const annoEff = annoSel != null ? annoSel : (annoGlobale != null ? annoGlobale + 1 : currentAnnoScolastico() + 1);
  const anniOpts = (() => {
    const set = new Set((anniAvailable || []).map(Number));
    set.add(annoEff);
    const base = currentAnnoScolastico();
    [base + 2, base + 1, base, base - 1, base - 2, base - 3].forEach((y) => set.add(y));
    return Array.from(set).sort((a, b) => b - a);
  })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const od = await api.get(`/tabelle-config/modelli`, { params: { solo_openday: 1 } });
      const ods = od.data.items || [];
      setOdTables(ods);
      setTab((prev) => {
        if (prev && ods.some((m) => String(m._id) === String(prev))) return prev;
        return ods[0] ? String(ods[0]._id) : null;
      });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const activeModello = tab ? odTables.find((m) => String(m._id) === String(tab)) : null;

  const stampaPeriodo = async () => {
    if (!da || !aDate) { alert("Seleziona il periodo (dal / al)."); return; }
    setPrinting(true);
    try {
      const r = await api.get(`/tabelle-config/iscrizioni/stampa-periodo`, { params: { anno: annoEff, da, a: aDate }, responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert("Errore nella stampa del periodo. Riprova.");
    } finally { setPrinting(false); }
  };

  const dInput = { padding: "7px 10px", border: "2px solid #558b2f", borderRadius: 8, fontSize: 14, fontWeight: 700, color: "#33691e", background: "white" };

  return (
    <div data-testid="openday-page" style={{ padding: 20 }}>
      <div className="sw-page-title"><h1>📝 Iscrizioni</h1><div className="sw-breadcrumb">Home &gt; Iscrizioni</div></div>
      <div data-testid="od-anno-box" style={{ display: "inline-flex", alignItems: "center", gap: 14, margin: "8px 0 18px", padding: "12px 20px", background: "#e3f2fd", border: "2px solid #0d3b66", borderRadius: 12, boxShadow: "0 2px 8px rgba(13,59,102,0.18)" }}>
        <label htmlFor="od-anno-filter" style={{ fontSize: 16, fontWeight: 800, color: "#0d3b66", letterSpacing: 0.3 }}>📅 Anno scolastico di iscrizione</label>
        <select id="od-anno-filter" data-testid="od-anno-filter" value={annoEff} onChange={(e) => setAnnoSel(Number(e.target.value))}
          style={{ padding: "10px 18px", border: "2px solid #0d3b66", borderRadius: 8, fontSize: 18, fontWeight: 800, color: "#0d3b66", background: "white", cursor: "pointer" }}>
          {anniOpts.map((y) => <option key={y} value={y}>{formatAnnoScolastico(y)}</option>)}
        </select>
      </div>

      <div data-testid="od-stampa-box" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, margin: "0 0 18px", padding: "12px 20px", background: "#f1f8e9", border: "2px solid #558b2f", borderRadius: 12, boxShadow: "0 2px 8px rgba(85,139,47,0.15)" }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: "#33691e" }}>🖨️ Stampa iscrizioni (tutte le tipologie) dal</span>
        <input type="date" data-testid="od-stampa-da" value={da} onChange={(e) => setDa(e.target.value)} style={dInput} />
        <span style={{ fontSize: 15, fontWeight: 800, color: "#33691e" }}>al</span>
        <input type="date" data-testid="od-stampa-a" value={aDate} onChange={(e) => setADate(e.target.value)} style={dInput} />
        <button data-testid="od-stampa-btn" onClick={stampaPeriodo} disabled={printing}
          style={{ padding: "8px 18px", background: printing ? "#9e9e9e" : "#558b2f", color: "white", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 800, cursor: printing ? "wait" : "pointer" }}>
          {printing ? "Genero…" : "Genera PDF"}
        </button>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: "2px solid #e0e0e0", flexWrap: "wrap" }}>
        {odTables.map((m) => (
          <button key={m._id} data-testid={`od-tab-${m._id}`} onClick={() => setTab(String(m._id))} style={tabStyle(tab === String(m._id))}>
            📋 {stripAnno(m.nome) || m.nome}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: "#90a4ae", padding: 20 }}>Caricamento…</div>
      ) : activeModello ? (
        <div key={`${activeModello._id}-${annoEff}`}><TableHost modello={activeModello} anno={annoEff} /></div>
      ) : (
        <div data-testid="od-empty" style={{ color: "#8d6e00", background: "#fff3e0", border: "1px solid #ffe082", borderRadius: 6, padding: 16, fontSize: 14, maxWidth: 700 }}>
          Ancora nessuna tabella iscrizioni disponibile.
        </div>
      )}
    </div>
  );
}
